import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader, PageShell } from "@/components/app/page";
import { adminKycAPI, authorAPI, profileAPI } from "@/services/api";
import { BookOpen, CheckCircle, Download, FileText, ShieldCheck, XCircle } from "lucide-react";
import { getProductCategoryLabel } from "@/utils/productCategories";

const STATUS_META = {
  pending: { label: "En cours de validation", className: "bg-amber-100 text-amber-800", icon: FileText },
  validated: { label: "validé", className: "bg-green-100 text-green-800", icon: CheckCircle },
  rejected: { label: "Rejete", className: "bg-red-100 text-red-800", icon: XCircle },
};

function productTypeLabel(type) {
  if (type === "texte_sonorise") return "Fichier audio";
  if (type === "texte_chante") return "Fichier video";
  return "Documents textes";
}

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}

export default function MesDocumentsPage({ user }) {
  const [profile, setProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const requests = [profileAPI.getMe()];
    if (user?.role === "partner") requests.push(authorAPI.listProducts());

    Promise.allSettled(requests)
      .then(([profileRes, productsRes]) => {
        if (cancelled) return;
        if (profileRes?.status === "fulfilled") setProfile(profileRes.value.data);
        if (productsRes?.status === "fulfilled") setProducts(Array.isArray(productsRes.value.data) ? productsRes.value.data : []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.role]);

  const currentUser = profile?.user || user;
  const kyc = profile?.kyc;
  const hasAgreement = kyc?.status === "approved" && currentUser?.is_verified;
  const sortedProducts = useMemo(
    () => [...products].sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""))),
    [products]
  );

  if (loading) {
    return <div className="py-10 text-sm text-[#5C625F]">Chargement de vos documents...</div>;
  }

  return (
    <PageShell data-testid="my-documents-page">
      <PageHeader
        title="Mes documents"
        description="Retrouvez vos oeuvres soumises, votre contrat MMJ et les documents juridiques de reference."
      />

      {currentUser?.role === "partner" && (
        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#2A4B46]">
              <BookOpen size={20} /> Mes livres et produits publies
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sortedProducts.length === 0 ? (
              <div className="rounded-lg border border-dashed border-[#D8D2C8] bg-[#FCFBF8] p-5 text-sm text-[#5C625F]">
                Aucun produit soumis pour le moment.
              </div>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {sortedProducts.map((product) => {
                  const status = STATUS_META[product.status] || STATUS_META.pending;
                  const StatusIcon = status.icon;
                  return (
                    <div key={product.id} className="rounded-lg border border-[#E6E4E0] bg-white p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="break-words text-sm font-bold text-[#1A1D1C]">{product.title}</p>
                          <p className="mt-1 text-xs font-semibold text-[#5C625F]">{productTypeLabel(product.product_type)}</p>
                        </div>
                        <Badge className={`shrink-0 gap-1 rounded-full ${status.className}`}>
                          <StatusIcon size={13} /> {status.label}
                        </Badge>
                      </div>
                      <p className="mt-3 line-clamp-3 text-sm leading-6 text-[#5C625F]">{product.description}</p>
                      <div className="mt-3 grid gap-2 text-xs text-[#6D7470] sm:grid-cols-2">
                        <span>Auteur : {product.author_name || currentUser?.full_name || "-"}</span>
                        <span>Categorie : {getProductCategoryLabel(product.category)}</span>
                        {product.subcategory && <span>Sous-categorie : {product.subcategory}</span>}
                        <span>Valeur : {Number(product.price_mc || 0) > 0 ? `${product.price_mc} MC` : "Libre d'accès"}</span>
                        <span>Soumis le : {formatDate(product.created_at)}</span>
                      </div>
                      {product.status === "rejected" && product.rejection_reason && (
                        <p className="mt-3 rounded-lg bg-red-50 p-3 text-xs text-red-800">{product.rejection_reason}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#2A4B46]">
              <ShieldCheck size={20} /> Contrat MMJ
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-lg border border-[#E6E4E0] bg-white px-4 py-3">
              <p className="text-sm font-semibold text-[#1A1D1C]">Agrement ARTICO-MMJ signe</p>
              <p className="mt-1 text-xs leading-5 text-[#5C625F]">
                {hasAgreement ? "Votre contrat validé est disponible au format PDF." : "Le contrat devient disponible après validation complete du KYC."}
              </p>
            </div>
            {hasAgreement ? (
              <a href={adminKycAPI.getAgreementPdfUrl(kyc.id)} target="_blank" rel="noreferrer">
                <Button className="w-full gap-2 bg-[#D4A373] text-white hover:bg-[#c08d5c]">
                  <Download size={18} /> Ouvrir mon contrat
                </Button>
              </a>
            ) : currentUser?.role === "partner" ? (
              <Link to="/verification">
                <Button variant="outline" className="w-full gap-2 border-orange-200 text-orange-800 hover:bg-orange-50">
                  <ShieldCheck size={18} /> Completer ma verification
                </Button>
              </Link>
            ) : null}
          </CardContent>
        </Card>

        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#2A4B46]">
              <FileText size={20} /> Confidentialite et conditions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-lg border border-[#E6E4E0] bg-white px-4 py-3">
              <p className="text-sm font-semibold text-[#1A1D1C]">Documents juridiques MMJ</p>
              <p className="mt-1 text-xs leading-5 text-[#5C625F]">
                accès direct aux conditions d'utilisation et a la politique de confidentialite acceptees lors de la creation du compte.
              </p>
            </div>
            <Link to="/legal">
              <Button variant="outline" className="w-full gap-2 border-[#E6E4E0] text-[#2A4B46] hover:bg-[#F3EFE9]">
                <FileText size={18} /> Consulter les documents
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </PageShell>
  );
}
